import { useCallback } from 'react'
import { FormApi } from 'final-form'
import { NavigateFunction } from 'react-router-dom'
import { when } from 'mobx'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { trackTeal } from 'utils/tracking'
import {
  AmplitudeEvent,
  ErrorType
} from 'utils/tracking/amplitude/amplitude.interface'
import { LoadingMode } from 'components/LoadingProvider'
import {
  CreditChangeOption,
  TraversalError
} from 'interfaces/ApolloResponse.interface'
import { valueComparer } from 'utils/tracking/amplitude/utils'
import { executeAsync } from 'utils/performance'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { isPrefillPossible } from 'components/AbTestComponents/CAT4197/utils'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { Experiments } from 'CustomOptimizelyProvider'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { useSetError } from 'context/Error/hook'
import { AxiosError } from 'axios'
import isNotMortgage from 'utils/formDependencies/conditions/isNotMortgage'
import { isTenantSmava } from 'utils/url'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { useMortgageRedirect } from 'hooks/useMortgageRedirect'

export const useFormSubmission = (
  navigate: NavigateFunction,
  currentPage?: string,
  captchaHandlers?: {
    handleCaptcha: (action: string) => void
    shouldHandleCaptcha: (action: string) => boolean
    shouldAwaitCaptcha: () => boolean
  }
) => {
  const { rootStore } = useRootStore()
  const {
    traversal,
    fieldErrors,
    navigation,
    captcha,
    prefill,
    experimentData,
    page: { getCurrentIndex, nextPageName, isOnTermsAndConditionsPage }
  } = useStores()
  const urlParams = useUrlParams()
  const setError = useSetError()
  const isSmava = isTenantSmava()

  // Use the dedicated mortgage redirect hook
  const { redirectToMortgage } = useMortgageRedirect()

  const handleSubmit = useCallback(
    async (_: any, form: FormApi) => {
      const shouldBroadcastRedirectEvent =
        isSmava &&
        !isNotMortgage(traversal.data, urlParams?.context) &&
        (currentPage === 'contact' || currentPage === 'base')

      trackInAmplitude(AmplitudeEvent.CtaIsClicked)
      rootStore.setLoadingMode(LoadingMode.Blocking)

      if (shouldBroadcastRedirectEvent) {
        redirectToMortgage()
        return null
      }

      const { data } = traversal
      const trackTplAmountAmountChanging =
        (data.debtors.primary.activeLoans?.length ||
          data.debtors.secondary.activeLoans?.length) &&
        data.common.manuallyAdaptedAmount &&
        data.common.amountAdaption === CreditChangeOption.other

      if (trackTplAmountAmountChanging) {
        const comparedValue = valueComparer(
          traversal.calculatedManuallyAdaptedAmount,
          data.common.manuallyAdaptedAmount
        )
        trackInAmplitude(AmplitudeEvent.TplManuallyAdaptedAmountRelation, {
          comparedValue
        })
      }

      try {
        const cat4197Variant =
          // @ts-ignore - experimentData type needs refinement for dynamic access
          experimentData[Experiments.CAT_4197_PREFILL_V2]
        const isOnTermsPage = await isOnTermsAndConditionsPage()

        if (isOnTermsPage && cat4197Variant !== PossibleVariants.VARIATION_B) {
          /* calling traversal patch directly so that we can await before the next put call */
          await traversal.patch([
            { op: 'set', path: '/common/hasAcceptedTerms', value: true }
          ])
          traversal.acceptTermsAndConditions()
        }

        await fieldErrors.setFieldErrorsFromAxiosResponse(await traversal.put())

        const errors = await fieldErrors.getErrors(currentPage || '')
        const erroredPaths = Object.keys(errors)

        const previousErrorPageName =
          await fieldErrors.findFirstErrorPage(getCurrentIndex())

        if (erroredPaths.length > 0) {
          executeAsync(() => {
            trackInAmplitude(AmplitudeEvent.ValidateTraversal, {
              type: ErrorType.Page,
              page: currentPage,
              errors: erroredPaths.join(',')
            })

            for (const errorPath of erroredPaths) {
              const traversalErrors = errors[errorPath]
              if (!traversalErrors) {
                continue
              }

              trackInAmplitude(AmplitudeEvent.ValidateTraversal, {
                type: ErrorType.Field,
                path: errorPath,
                errors: traversalErrors
                  .map(({ id }: TraversalError) => id)
                  .join(',')
              })
            }
          })

          const visibleFieldPaths = form.getRegisteredFields()
          const visibleAndErrored = erroredPaths.filter(
            (x) => !visibleFieldPaths.includes(x)
          )

          visibleAndErrored.forEach((path) => delete errors[path])

          rootStore.setLoadingMode(LoadingMode.None)
          return errors
        }

        if (previousErrorPageName) {
          rootStore.setLoadingMode(LoadingMode.None)

          const errorsOnFirstBrokenPage = await fieldErrors.getErrors(
            previousErrorPageName
          )
          return errorsOnFirstBrokenPage
        }

        const isAddUserPropertiesOnPut = isTenantSmava()
        if (isAddUserPropertiesOnPut) {
          rootStore.traversal.setUserPropertiesOnPut()
        }

        const isOnLeadPage = currentPage === 'contact'

        if (
          isOnLeadPage &&
          traversal.traversalId &&
          cat4197Variant &&
          isPrefillPossible(traversal.traversalId)
        ) {
          const {
            system: { advertisementId }
          } = data
          const isVirtual = cat4197Variant !== PossibleVariants.VARIATION_B

          try {
            await prefill.getIsPrefillAvailable(
              advertisementId,
              traversal.traversalId,
              isVirtual
            )
            trackTeal({
              transaction: {
                event: AmplitudeEvent.PrefillCheck
              },
              payload: {
                eligible: true
              },
              context: {
                Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
                userAttributes: {
                  advertisementId
                }
              }
            })
          } catch (err) {
            const errorObj = (err ?? {}) as any
            trackTeal({
              transaction: {
                event: AmplitudeEvent.PrefillCheck
              },
              payload: {
                eligible: false,
                timeout: errorObj?.timeout || undefined,
                errStatus: errorObj?.status || 'unknown',
                ...(errorObj.rejection_reason
                  ? { reason: errorObj.rejection_reason }
                  : {})
              },
              context: {
                Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
                userAttributes: {
                  advertisementId
                }
              }
            })
          }
        }
        // the current implementation to track lead does not work
        if (isOnLeadPage && !prefill.isPrefillAvailable) {
          /* calling traversal patch directly so that we can await before the next put call */
          await traversal.patch([
            { op: 'set', path: '/common/hasAcceptedTerms', value: true }
          ])
          traversal.acceptTermsAndConditions()
        }

        if (captchaHandlers?.shouldHandleCaptcha('ctaClick')) {
          captchaHandlers.handleCaptcha('ctaClick')

          if (captchaHandlers.shouldAwaitCaptcha()) {
            try {
              await when(
                () =>
                  captcha.captchaFlowStatus === 'success' ||
                  captcha.captchaFlowStatus === 'error',
                {
                  timeout: 120000
                }
              )
            } catch (err) {
              logAndTrackError(new Error('Captcha timeout or error'))
              trackInAmplitude(AmplitudeEvent.CaptchaTimeoutError, {
                error: String(err),
                errObject: err
              })
            }
          }
        }

        await navigation.goToNextPage(navigate)
      } catch (error) {
        rootStore.setLoadingMode(LoadingMode.None)
        const axiosError = error as AxiosError
        setError(axiosError)
        trackInAmplitude(AmplitudeEvent.ErrorOccurred, {
          error: axiosError?.message
        })
      }

      return {}
    },
    [
      navigate,
      rootStore,
      traversal,
      fieldErrors,
      navigation,
      currentPage,
      isSmava,
      urlParams,
      redirectToMortgage,
      captchaHandlers,
      captcha,
      experimentData,
      prefill,
      getCurrentIndex,
      nextPageName,
      setError
    ]
  )

  return { handleSubmit }
}
